import { CommandsBase } from './base';
import { Boards } from 'src/api';
import { Store as VuexStore } from 'vuex';
import { StateInterface } from 'src/store';

export namespace Distribution {
  export namespace Types {
    export interface Execution {
      boardId: number;
      id: string; // arn
      error: boolean;
      step: string;
      timestamp: string;
      message: string;
    }

    export interface ExecutionsProps {
      dayOffset: number;
    }

    export interface BoardExecutionsProps extends ExecutionsProps {
      boardId: number;
    }

    export type ExecutionsResult = Execution[];

    export interface ExecutionStatusProps {
      executionArn: string;
    }

    export interface ExecutionStatusResponse {
      postingCount: number;
      error: string;
      done: boolean;
    }

    export type DeliveryStatusType = 'local' | 'global';

    export interface DeliveryStatusProps {
      active: boolean;
      type: DeliveryStatusType;
    }

    export interface DeliveryStatusResponse {
      activeGlobalIds: number[];
      activeLocalIds: number[];
    }

    export interface DeliveryUpdateResponse extends DeliveryStatusResponse {
      active: boolean;
      type: DeliveryStatusType;
      updatedBoardIds: number[];
    }

    // todo: should me moved to SDK for reusing with API
    // API contain the same in the handler get-pending-postings-url.ts
    export interface PendingPostingInfoProps {
      dayOffset?: number;
      boardId?: number;
      excludeLocalOutreachBoards?: boolean;
    }

    export interface PendingPostingInfoResponse {
      count: number;
      url: string;
    }

    export const EXECUTION_STEPS = {
      CONFIRMATION: 'confirmation',
      DELIVERY: 'deliver',
      VALIDATION: 'prepare',
    };

    export type ExecutionStep = 'confirmation' | 'deliver' | 'prepare';

    export type EventHandler = (
      data: Execution[],
      store: VuexStore<StateInterface>,
      context: { boardId: number | null }
    ) => Execution[];
  }

  export const SERVICE = 'distribution';
  export const ACTIONS = {
    executions: 'executions',
    getStatus: 'getStatus',
    setStatus: 'setStatus',
    executionStatus: 'executionStatus',
    getPendingPostingsInfo: 'getPendingPostingsInfo',
    // statistics: distribution.getStatistics,
    // getFeedSample: distribution.generateBoardFeedSample,
    // batchActionTrigger: distribution.boardBatchActionTrigger,
    // batchActionExecutions: distribution.boardBatchActionExecutions,
    // batchActionAbort: distribution.boardBatchActionAbort,
  };

  export const EVENT_HANDLERS: { [eventName: string]: Types.EventHandler } = {
    addExecutionRecords: (executions, store, context) => {
      let data = executions;

      // filter new executions for non 'today' period
      const period = store.state.distribution.dayOffset;
      if (period) {
        const todayDate = new Date().getDate();
        data = data.filter(
          (e) => new Date(e.timestamp).getDate() === todayDate
        );
      }

      if (context.boardId) {
        return data.filter((r) => r.boardId === context.boardId);
      }

      return data.filter((r) => r.error);
    },
  };

  export const SKIP_EVENTS = [
    'status',
    'settings',
    'scheduling',
    // 'addExecutionRecords',
    'setExecutionRecords',
    ACTIONS.executionStatus,
    ACTIONS.getStatus,
  ];

  export class Commands extends CommandsBase {
    loadExecutions(data: Types.ExecutionsProps) {
      return this.client.execute<Types.ExecutionsProps, Types.ExecutionsResult>(
        this.command(ACTIONS.executions),
        data
      );
    }

    // fixme: rename service/action to 'distribution/boardExecutions'
    // fixme: rename method to boardExecutions???
    loadBoardExecutions(data: Types.BoardExecutionsProps) {
      return this.client.execute<
        Types.BoardExecutionsProps,
        Types.ExecutionsResult
      >(this.command(ACTIONS.executions, Boards.SERVICE), data);
    }

    executionStatus(data: Types.ExecutionStatusProps) {
      return this.client.execute<
        Types.ExecutionStatusProps,
        Types.ExecutionStatusResponse
      >(this.command(ACTIONS.executionStatus), data);
    }

    setDeliveryStatus(data: Types.DeliveryStatusProps) {
      return this.client.execute<
        Types.DeliveryStatusProps,
        Types.DeliveryUpdateResponse
      >(this.command(ACTIONS.setStatus), data);
    }

    getDeliveryStatus() {
      return this.client.execute<
        Record<string, unknown>,
        Types.DeliveryStatusResponse
      >(this.command(ACTIONS.getStatus), {});
    }

    getPendingPostingsInfo(data: Types.PendingPostingInfoProps) {
      return this.client.execute<
        Types.PendingPostingInfoProps,
        Types.PendingPostingInfoResponse
      >(this.command(ACTIONS.getPendingPostingsInfo), data);
    }
  }
}
